import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import {
  Home,
  Newspaper,
  Video,
  Headphones,
  BookOpen,
  Briefcase,
  Quote,
  BarChart2,
  User,
  ChartBar,
  Users,
  NotebookText,
  CircleQuestionMark,
  Sparkles,
} from "lucide-react";
import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { saffronURL } from "../../Url";

const iconMap = {
  home: Home,
  news: Newspaper,
  video: Video,
  audio: Headphones,
  blog: BookOpen,
  business: Briefcase,
  quotes: Quote,
  polls: BarChart2,
  profile: User,
  quote: Quote,
  poll: ChartBar,
  team: Users,
  ebook: NotebookText,
  faq: CircleQuestionMark,
  feature: Sparkles,
};

export default function RightMenubar({
  selectedCountry,
  selectedState,
  selectedCity,
  selectedLanguage,
  selectedCategory,
}) {
  const location = useLocation(); // ✅ keep query params during nav

  const { data: menuData, isLoading } = useQuery({
    queryKey: [
      "menuData_connect",
      selectedCountry,
      selectedLanguage,
      selectedCategory,
      selectedState,
      selectedCity,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCountry) params.append("country", selectedCountry);
      if (selectedLanguage) params.append("language", selectedLanguage);
      if (selectedCategory) params.append("category", selectedCategory);
      if (selectedState) params.append("state", selectedState);
      if (selectedCity) params.append("city", selectedCity);

      const url = `${saffronURL}/api/v1/public/menu?${params.toString()}`;
      const resp = await axios.get(url);
      return resp.data;
    },
    enabled: !!selectedCountry, // ✅ country default always exists
    retry: 2,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 60, // ✅ v5 (use cacheTime if v4)
  });

  const keepKeys = ["country", "state", "city", "language", "category"]; // ✅ only filters

  const buildSafeSearch = () => {
    const current = new URLSearchParams(location.search);
    const next = new URLSearchParams();

    keepKeys.forEach((k) => {
      const v = current.get(k);
      if (v !== null && v !== "") next.set(k, v);
    });

    return next.toString() ? `?${next.toString()}` : "";
  };

  const detailsActiveKey = (type) => {
    const t = (type || "").toLowerCase();

    const map = {
      people: "profile",
      profile: "profile",

      news: "news",
      trend_news: "news",
      latest_news: "news",
      featured_news: "news",

      video: "video",
      videos: "video",

      audio: "audio",
      audios: "audio",

      blog: "blog",
      blogs: "blog",

      ebook: "ebook",
      ebooks: "ebook",

      listing: "business",
      listings: "business",

      quote: "quote",
      quotes: "quote",

      poll: "poll",
      polls: "poll",

      webstory: "webstory",
      webstories: "webstory",

      feature: "feature",
      features: "feature",
      team: "team",
      teams: "teams",
    };

    return map[t] || null;
  };

  const isDetails = location.pathname.includes("/details/");
  const typeFromDetails = isDetails
    ? location.pathname.split("/details/")[1]?.split("/")[0]
    : null;
  const activeKey = isDetails ? detailsActiveKey(typeFromDetails) : null;

  return (
    <aside
      className="
        max-lg:fixed bottom-0 left-0 right-0 
       lg:left-auto w-full
        bg-white border-t lg:border-l lg:border-t-0
        shadow-sm flex items-center  max-lg:justify-between
        lg:flex-col lg:py-6 lg:space-y-6
        overflow-x-auto lg:overflow-y-auto
        scrollbar-hide
         z-[999] lg:z-[1]
      "
    >
      {/* {menuData?.data?.items?.filter(res => res.key !== 'webstory')?.map((item, i) => { */}

      {menuData?.data?.items?.map((item, i) => {
        const Icon = iconMap[item.key] || Home;

        return (
          <NavLink
            key={i}
            // ✅ home should go to "." (parent index), not "home" route unless you created it
            to={{
              pathname: item.key === "home" ? "home" : item.key,
              search: buildSafeSearch(), // ✅ KEEP FILTER PARAMS
            }}
            end={item.key === "home"} // ✅ exact active on base route
            className={({ isActive }) => {
              const active = isActive || (activeKey && item.key === activeKey);

              return `group flex flex-col items-center
    my-2 lg:my-0 mx-4 lg:mx-0
    cursor-pointer transition
    ${
      active
        ? "text-orange-500 bg-orange-50 rounded-md px-4 md:px-2 py-2"
        : "text-gray-600 hover:text-orange-500"
    }`;
            }}
          >
            {({ isActive }) => {
              const active = isActive || (activeKey && item.key === activeKey);

              return (
                <>
                  <Icon size={22} />
                  <span
                    className={`mt-1 text-xs font-medium whitespace-nowrap md:whitespace-normal text-center ${
                      active ? "text-orange-500" : "group-hover:text-orange-500"
                    }`}
                  >
                    {item.label}
                  </span>
                </>
              );
            }}
          </NavLink>
        );
      })}
    </aside>
  );
}
